/* eslint-disable react/prop-types */
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useEffectAsync } from "lib/react/customHooks";
import {
  Grid,
  Box,
  makeStyles,
  useMediaQuery,
  Tooltip,
  Typography,
} from "@material-ui/core";
import _ from "lodash";
import { getQueryFilters } from "lib/utils";
import CardComponent from "app/common/CardComponent";
import NoResultsComponent from "app/common/NoResultsComponent";
import BorderLinearProgress from "app/common/BorderLinearProgress";
import getElasticities from "lib/requests/elasticities";
import ExplicabilityComponent from "../explicability-info/ExplicabilityComponent";
import { setTupleData } from "./duck/slices";

const useStyles = makeStyles((theme) => ({
  crossEffectsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    flex: 1,
  },
  crossEffectItem: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("lg")]: {
      marginTop: "5px",
      marginBottom: "5px",
    },
  },
  paddingRight: {
    paddingRight: "10px",
  },
  paddingLeft: {
    paddingLeft: "10px",
  },
  paddingTop: {
    paddingTop: "10px",
  },
  sectionsContainer: {
    flexGrow: 1,
  },
  sectionContainer: {
    marginTop: "5px",
    marginBottom: "5px",
    display: "flex",
    flexDirection: "column",
  },
  sectionTitle: {
    borderBottom: "1px solid #e2e2e2",
    paddingBottom: "5px",
    fontSize: "11px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "14px",
    },
  },
  crossEffectTitle: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "10px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "13px",
    },
  },
  progressBarOwnProducts: { backgroundColor: "#10A2B1" },
  progressBarOwnProductsLight: { backgroundColor: "rgba(16, 162, 177, 0.5)" },
  progressBarCompetitorProducts: { backgroundColor: "#FFC84A" },
  progressBarCompetitorProductsLight: {
    backgroundColor: "rgba(255, 200, 74, 0.5)",
  },
  componentTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
    },
  },
}));

function CompetitorsCrossEffectComponent() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const filtersSlice = useSelector((state) => state.filters);
  const competitorsSlice = useSelector((state) => state.competitors);
  const catalogSlice = useSelector((state) => state.catalog);
  const [crossEffectsData, setCrossEffectsData] = React.useState(null);
  const sidebarFilters = _.pick(filtersSlice.filters, [
    "category",
    "retailer",
    "area",
  ]);
  const sidebarQueryFilters = getQueryFilters(sidebarFilters);

  const retrieveCrossEffectsForTuple = async (tupleKey) => {
    if (
      competitorsSlice.tuples[tupleKey] &&
      competitorsSlice.tuples[tupleKey].crossEffects
    ) {
      return {
        key: tupleKey,
        crossEffects: competitorsSlice.tuples[tupleKey].crossEffects,
      };
    }

    const currentDate = new Date();

    const queryFilters = {
      ...sidebarQueryFilters,
      data_source_id: 2,
      min_date: currentDate.toISOString().slice(0, 10),
      max_date: new Date(currentDate.getFullYear(), currentDate.getMonth() + 12)
        .toISOString()
        .slice(0, 10),
      sum: ["constant"],
      order_by: ["sum_constant"],
      order_direction: ["desc"],
      limit: ["3"],
    };

    // My products affecting competitors
    const myProductsAffectingCompetitorsParams = {
      filters: {
        ...queryFilters,
        is_source_product_client: 1,
        is_product_client: 0,
      },
      group_by: ["source_product_id"],
      join_by: ["source"],
    };

    // My products affected by competitors
    const myProductsAffectedByCompetitorsParams = {
      filters: {
        ...queryFilters,
        is_source_product_client: 0,
        is_product_client: 1,
      },
      group_by: ["product_id"],
      join_by: ["target"],
    };

    // Competitors affecting my products
    const competitorsAffectingMyProductsParams = {
      filters: {
        ...queryFilters,
        is_source_product_client: 0,
        is_product_client: 1,
      },
      group_by: ["source_product_id"],
      join_by: ["target"],
    };

    // Competitors affected by my products
    const competitorsAffectedByMyProductsParams = {
      filters: {
        ...queryFilters,
        is_source_product_client: 1,
        is_product_client: 0,
      },
      group_by: ["product_id"],
      join_by: ["source"],
    };

    const [
      myProductsAffectingCompetitorsResult,
      myProductsAffectedByCompetitorsResult,
      competitorsAffectingMyProductsResult,
      competitorsAffectedByMyProductsResult,
    ] = await Promise.all([
      getElasticities(myProductsAffectingCompetitorsParams),
      getElasticities(myProductsAffectedByCompetitorsParams),
      getElasticities(competitorsAffectingMyProductsParams),
      getElasticities(competitorsAffectedByMyProductsParams),
    ]);

    return {
      key: tupleKey,
      crossEffects: {
        myProductsAffectingCompetitors:
          myProductsAffectingCompetitorsResult.data,
        myProductsAffectedByCompetitors:
          myProductsAffectedByCompetitorsResult.data,
        competitorsAffectingMyProducts:
          competitorsAffectingMyProductsResult.data,
        competitorsAffectedByMyProducts:
          competitorsAffectedByMyProductsResult.data,
      },
    };
  };

  useEffectAsync(
    async () => {
      const tupleKey = `crosseffects-${filtersSlice.filtersKey}`;

      return retrieveCrossEffectsForTuple(tupleKey);
    },
    (result) => {
      const tupleData = {
        crossEffects: result.crossEffects,
      };

      dispatch(
        setTupleData({
          [result.key]: tupleData,
        })
      );

      setCrossEffectsData(tupleData);
    },
    [filtersSlice.filters]
  );

  const crossEffectLayoutSchemas = [
    {
      title: (
        <Typography variant="body1" className={classes.sectionTitle}>
          <span style={{ color: "#10A2B1" }}>My products</span> affecting
          competitors
        </Typography>
      ),
      key: "myProductsAffectingCompetitors",
      target: "products",
      origin: "source_product_id",
      barClass: classes.progressBarOwnProducts,
      padding: classes.paddingRight,
    },
    {
      title: (
        <Typography variant="body1" className={classes.sectionTitle}>
          <span style={{ color: "#FFC84A" }}>Competitors</span> affecting my
          products
        </Typography>
      ),
      key: "competitorsAffectingMyProducts",
      target: "competitors",
      origin: "source_product_id",
      barClass: classes.progressBarCompetitorProducts,
      padding: classes.paddingLeft,
    },
    {
      title: (
        <Typography variant="body1" className={classes.sectionTitle}>
          <span style={{ color: "#10A2B1" }}>My products</span> affected by
          competitors
        </Typography>
      ),
      key: "myProductsAffectedByCompetitors",
      target: "products",
      origin: "product_id",
      barClass: classes.progressBarOwnProductsLight,
      padding: classes.paddingRight,
    },
    {
      title: (
        <Typography variant="body1" className={classes.sectionTitle}>
          <span style={{ color: "#FFC84A" }}>Competitors</span> affected by my
          products
        </Typography>
      ),
      key: "competitorsAffectedByMyProducts",
      target: "competitors",
      origin: "product_id",
      barClass: classes.progressBarCompetitorProductsLight,
      padding: classes.paddingLeft,
    },
  ];

  return (
    <>
      {crossEffectsData && (
        <CardComponent
          title={
            <Typography variant="body1" className={classes.componentTitle}>
              Competitors&#39; Cross Effects
              <ExplicabilityComponent tooltipText="Check  the most relevant products to beat your competitors and check your own products to fast react to competitors price changes" />
            </Typography>
          }
          body={
            !crossEffectsData.crossEffects.myProductsAffectingCompetitors
              .length &&
            !crossEffectsData.crossEffects.competitorsAffectingMyProducts
              .length &&
            !crossEffectsData.crossEffects.myProductsAffectedByCompetitors
              .length &&
            !crossEffectsData.crossEffects.competitorsAffectedByMyProducts
              .length ? (
              <NoResultsComponent />
            ) : (
              <Grid container className={classes.sectionsContainer}>
                {_.map(crossEffectLayoutSchemas, (schema) => {
                  return (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      lg={6}
                      className={`${
                        classes.sectionContainer
                      } ${!isSmallScreen && schema.padding}`}
                    >
                      {schema.title}

                      <Box className={classes.crossEffectsContainer}>
                        {!crossEffectsData.crossEffects[schema.key].length ? (
                          <NoResultsComponent size="small" />
                        ) : (
                          _.map(
                            crossEffectsData.crossEffects[schema.key],
                            (crossEffect) => {
                              return (
                                <Grid
                                  container
                                  spacing={2}
                                  className={classes.crossEffectItem}
                                >
                                  <Grid item xs={6} sm={6} md={6} lg={6}>
                                    <Tooltip
                                      title={
                                        catalogSlice[schema.target][
                                          crossEffect[schema.origin]
                                        ].label
                                      }
                                      placement="top-start"
                                    >
                                      <Typography
                                        variant="body2"
                                        className={classes.crossEffectTitle}
                                      >
                                        {
                                          catalogSlice[schema.target][
                                            crossEffect[schema.origin]
                                          ].label
                                        }
                                      </Typography>
                                    </Tooltip>
                                  </Grid>
                                  <Grid item xs={6} sm={6} md={6} lg={6}>
                                    <BorderLinearProgress
                                      variant="determinate"
                                      value={
                                        (100 * crossEffect.sum_constant) /
                                        _.max(
                                          _.map(
                                            crossEffectsData.crossEffects[
                                              schema.key
                                            ],
                                            (effect) => effect.sum_constant
                                          )
                                        )
                                      }
                                      classes={{
                                        bar: schema.barClass,
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              );
                            }
                          )
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )
          }
        />
      )}
    </>
  );
}

export default CompetitorsCrossEffectComponent;
